import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import type { SortDescriptor } from '../../components/Table';
import type { Database } from '../../types/database';
import {
  PAGE_SIZE,
  defaultState,
  hasActiveFilters,
  parseSearchParams,
  stateToSearchParams,
  type DocumentLifecycle,
  type DocumentListState,
  type DocumentRow,
  type DocumentsGroupBy,
  type DocumentsTab,
  type FilterFacetKey
} from '../models/documents';

type ListDocumentsArgs = Database['public']['Functions']['list_documents']['Args'];
type DocumentPropertyDefRow =
  Database['public']['Tables']['document_property_defs']['Row'];
type DocumentPropertyOptionRow =
  Database['public']['Tables']['document_property_options']['Row'];

/**
 * Workspace-pool property key for the three system rows that used to live as
 * hardcoded `documents` columns. After Phase 1–4 they're stored as
 * `document_property_defs` rows with these keys (positions -3 / -2 / -1).
 */
const SYSTEM_OWNER_KEY = 'owner';
const SYSTEM_LIFECYCLE_KEY = 'lifecycle';
const SYSTEM_SUBTYPE_KEY = 'subtype';

/**
 * ViewModel for `/documents`.
 *
 * Owns the URL-state contract end-to-end. On mount the VM hydrates `state` from
 * the route-scoped query (react-router `useSearchParams`). Every action mutates
 * state; an effect mirrors it back to the route's query via `setSearchParams`
 * (route-scoped — not the cross-route `window.location.search`) and the change
 * triggers a re-fetch.
 *
 * Fetches through `supabase.rpc('list_documents', …)` — full-text +
 * trigram-fallback search, server-side facet filtering (type / status /
 * Owner / lifecycle / created+updated date bounds), sort, and a
 * windowed `total_count` for pagination in a single round-trip.
 */

export type DocumentsListStatus =
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'empty'
  | 'error';

export interface DocumentTypeOption {
  id: string;
  name: string;
  key: string;
}

export interface DocumentMemberOption {
  userId: string;
  displayName: string;
}

export interface DocumentSubtypeOption {
  id: string;
  name: string;
  slug: string;
}

/**
 * Per-row resolution of the three system properties (Owner / Lifecycle /
 * Subtype) after Phases 1–4. Reads come from `custom_attribute_values` keyed
 * by `(document_id, property_def_id)` of the org's workspace-pool def with
 * `key in ('owner','lifecycle','subtype')`.
 */
export interface DocumentSystemRowValues {
  /** `user_id` of the assigned Owner, or `null` when unset. */
  ownerId: string | null;
  /** Well-known lifecycle option key (`'draft'`/`'published'`/`'archived'`),
   *  or `null` when no option is selected. */
  lifecycle: DocumentLifecycle | null;
  /** `id` of the Subtype option, or `null` when unset. */
  subtypeId: string | null;
}

export interface DocumentsListDerived {
  rows: ReadonlyArray<DocumentRow>;
  total: number;
  pageSize: number;
  typeOptions: ReadonlyArray<DocumentTypeOption>;
  memberOptions: ReadonlyArray<DocumentMemberOption>;
  subtypeOptions: ReadonlyArray<DocumentSubtypeOption>;
  loading: boolean;
  error: string | null;
  hasActiveFilters: boolean;
  /** Whether the viewer's permission group grants `documents.create`. */
  canCreate: boolean;
  /** Map<document_id, DocumentSystemRowValues> — resolved values for the
   *  Owner / Lifecycle / Subtype system rows on each visible document. */
  systemValuesByDoc: ReadonlyMap<string, DocumentSystemRowValues>;
}

export interface DocumentsListActions {
  /** Replace a multi-value facet, or a date facet, or the lifecycle multi-value. */
  setFilter: (
    key: FilterFacetKey,
    value: ReadonlyArray<string> | string | null
  ) => void;
  /** Remove a single value from a multi-value facet, or clear a date facet. */
  removeFilter: (key: FilterFacetKey, value?: string) => void;
  setSort: (sort: SortDescriptor) => void;
  setSearch: (q: string) => void;
  setPage: (page: number) => void;
  setTab: (tab: DocumentsTab) => void;
  setGroupBy: (groupBy: DocumentsGroupBy) => void;
  setCols: (cols: ReadonlyArray<string>) => void;
  /** Archive a row via `archive_document` RPC. Optimistic when archived rows
   *  are not in the active lifecycle filter; refetch on success either way. */
  archiveDocument: (id: string) => Promise<{ ok: boolean; error?: string }>;
  /** Bulk-archive: invokes `archive_document` for each id (Promise.all). */
  archiveDocuments: (
    ids: ReadonlyArray<string>
  ) => Promise<{ ok: boolean; failed: ReadonlyArray<string> }>;
  /** Restore a row via `restore_document` RPC. Refetch on success. */
  restoreDocument: (id: string) => Promise<{ ok: boolean; error?: string }>;
  /** Set the lifecycle on a single document (draft/published/archived).
   *  Upserts `custom_attribute_values` keyed by `(document_id, lifecycle-def-id)`. */
  setLifecycle: (
    id: string,
    lifecycle: DocumentLifecycle
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Set the subtype on a single document. Pass null to clear.
   *  Upserts `custom_attribute_values` keyed by `(document_id, subtype-def-id)`. */
  setSubtype: (
    id: string,
    subtypeId: string | null
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Set the Owner (DRI) on a single document. Pass null to clear.
   *  Upserts `custom_attribute_values` keyed by `(document_id, owner-def-id)`. */
  setOwner: (
    id: string,
    userId: string | null
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Set the document's Type via the `set_document_type` RPC. Type is required
   *  (documents.type_id is NOT NULL) so there is no clear. Optimistically
   *  patches the row's `type_id`; reverts + refetches on error. */
  setDocumentType: (
    id: string,
    typeId: string
  ) => Promise<{ ok: boolean; error?: string }>;
  /** Create a document via `create_document` RPC. */
  createDocument: (input: {
    title: string;
    typeId?: string | null;
  }) => Promise<{ ok: true; id: string } | { ok: false }>;
  /** Re-fetch the current page in place (no remount → scroll preserved). */
  reload: () => void;
}

export interface UseDocumentsListVM {
  status: DocumentsListStatus;
  state: DocumentListState;
  activeOrgId: string | null;
  activeOrgName: string | null;
  derived: DocumentsListDerived;
  actions: DocumentsListActions;
}

/**
 * Build the `list_documents` RPC arg payload from the current URL state.
 * Empty / default values are omitted so the RPC's own defaults apply
 * (Postgres treats the missing args as NULL → unfiltered for that facet).
 *
 * Sort encoding: the URL uses `?sort=[-]<column>` (Work Items style); the RPC
 * expects the legacy `<column>_<dir>` strings so we translate here. The
 * RPC's supported axes are last_event_at / created_at / title.
 */
function buildRpcArgs(
  state: DocumentListState,
  orgId: string,
  projectId?: string | null
): ListDocumentsArgs {
  const args: ListDocumentsArgs = {
    p_org_id: orgId,
    p_sort: rpcSortFor(state.sort),
    p_page: state.page,
    p_page_size: PAGE_SIZE,
    p_include_archived: state.lifecycle.includes('archived')
  };
  if (projectId) {
    (args as ListDocumentsArgs & { p_project_id?: string }).p_project_id = projectId;
  }
  const trimmedQ = state.q.trim();
  if (trimmedQ) args.p_q = trimmedQ;
  if (state.type.length > 0) args.p_type_ids = [...state.type];
  if (state.owner.length > 0) {
    (args as ListDocumentsArgs & { p_owner_ids?: string[] }).p_owner_ids = [
      ...state.owner
    ];
  }
  // Lifecycle facet (state.lifecycle) is not yet threaded through
  // `list_documents`. After Phase 3 the lifecycle column moved into
  // custom_attribute_values; restoring server-side filtering needs a JOIN
  // through custom_attribute_values in the RPC body. Tracked separately —
  // the URL still round-trips so the filter chip survives a refresh, but
  // the row set comes back unfiltered. `state.lifecycle.includes('archived')`
  // still gates `p_include_archived` via the documents.archived_at column,
  // which is the only lifecycle signal the RPC currently honours.
  if (state.createdAfter) args.p_created_after = state.createdAfter;
  if (state.createdBefore) args.p_created_before = state.createdBefore;
  if (state.updatedAfter) args.p_updated_after = state.updatedAfter;
  if (state.updatedBefore) args.p_updated_before = state.updatedBefore;
  return args;
}

function rpcSortFor(sort: SortDescriptor): string {
  const dir = sort.direction === 'desc' ? 'desc' : 'asc';
  switch (sort.columnKey) {
    case 'last_event_at':
      return `last_event_at_${dir}`;
    case 'created_at':
      return `created_at_${dir}`;
    case 'title':
      return `title_${dir}`;
    default:
      return `last_event_at_${dir}`;
  }
}

/** Coerce the RPC's lifecycle option-key text into the well-known union. */
function asLifecycle(v: string | null): DocumentLifecycle | null {
  return v === 'draft' || v === 'published' || v === 'archived' ? v : null;
}

/** Shape of one row returned by `list_documents` — the document columns plus
 *  the inline-projected system properties (owner / lifecycle / subtype) and
 *  the windowed total count. */
interface ListDocumentsRow extends DocumentRow {
  owner_id: string | null;
  lifecycle: string | null;
  subtype_id: string | null;
  total_count: number;
}

type UrlMode = 'global' | 'local';

/**
 * Optional initial-state seam. The VM hydrates `state` once on mount from the
 * route-scoped query (react-router `useSearchParams`, which under HashRouter
 * targets the URL fragment's `?…`). The host page may pass a partial override
 * (e.g. a localStorage-restored `tab`, or a deep-link like
 * `#/documents?lifecycle=archived`) applied on top of the parsed query. Only the
 * keys present override the parsed defaults; everything else is unchanged.
 */
export type DocumentsListInitialOverrides = Partial<DocumentListState>;

export interface UseDocumentsListVMOptions {
  /** Seed overrides applied once on mount (router-aware deep-link seam). */
  overrides?: DocumentsListInitialOverrides;
  /** When set, scopes every list query to a single project (org AND project). */
  projectId?: string | null;
  /**
   * 'global' (default): the VM owns the route-scoped query — mount-read +
   * state→query mirror via `useSearchParams`/`setSearchParams`. 'local': fully
   * headless — no URL read/write — for embedding inside another page (e.g. the
   * project canvas) without polluting its URL. Mirrors the work-items
   * useLocal/useUrl view-state split.
   */
  urlMode?: UrlMode;
}

export function useDocumentsListVM(
  options?: UseDocumentsListVMOptions
): UseDocumentsListVM {
  const overrides = options?.overrides;
  const projectId = options?.projectId ?? null;
  const urlMode: UrlMode = options?.urlMode ?? 'global';

  // The URL query is owned by react-router (`useSearchParams`), which under the
  // app's HashRouter reads/writes the route-scoped fragment query (`#/documents?…`)
  // — NOT `window.location.search`, the single query string shared across every
  // route. Reading the shared search leaked this page's `cols`/`tab` into sibling
  // list pages (e.g. /projects) on navigation. 'local' mode stays fully headless.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const initialState = useMemo<DocumentListState>(() => {
    const base = urlMode === 'local' ? defaultState : parseSearchParams(searchParams);
    return overrides ? { ...base, ...overrides } : base;
    // Initial-state seam: evaluated once on mount. Later option changes are
    // intentionally ignored — URL/list state is owned by the VM from here on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, setState] = useState<DocumentListState>(initialState);

  // Mirror state → the route-scoped URL query (no-op in 'local' mode so the
  // project canvas stays URL-silent). Equality-gated so it's idempotent under
  // StrictMode and never fights an external back/forward navigation.
  useEffect(() => {
    if (urlMode === 'local') return;
    const desired = stateToSearchParams(state);
    const current = stateToSearchParams(parseSearchParams(searchParamsRef.current));
    if (desired.toString() !== current.toString()) {
      setSearchParams(desired, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, urlMode, setSearchParams]);
  const [status, setStatus] = useState<DocumentsListStatus>('loading');
  // The active org is owned by the shell (see `useActiveOrg`). This VM is a
  // pure consumer: it scopes every query to whatever org the shell says is
  // active and re-loads when the user switches. It must NOT re-derive its own
  // "oldest membership" org — that was the cross-org leak.
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [rows, setRows] = useState<ReadonlyArray<DocumentRow>>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeOptions, setTypeOptions] = useState<
    ReadonlyArray<DocumentTypeOption>
  >([]);
  const [memberOptions, setMemberOptions] = useState<
    ReadonlyArray<DocumentMemberOption>
  >([]);
  const [subtypeOptions, setSubtypeOptions] = useState<
    ReadonlyArray<DocumentSubtypeOption>
  >([]);
  const [canCreate, setCanCreate] = useState(false);
  const currentUserIdRef = useRef<string | null>(null);

  // System property defs (Owner / Lifecycle / Subtype) — the org-scoped
  // workspace-pool rows that Phases 2–4 introduced. Keyed by the well-known
  // `key`. Inline-cell edits write through these via
  // `custom_attribute_values.upsert({ document_id, property_def_id })`.
  const systemDefsRef = useRef<Map<string, DocumentPropertyDefRow>>(new Map());
  // Option pools for the system defs (Lifecycle + Subtype only — Owner is
  // `kind='user'` and resolves against memberships). Keyed by def.id.
  const systemOptionsRef = useRef<Map<string, DocumentPropertyOptionRow[]>>(
    new Map()
  );
  // Resolved Owner / Lifecycle / Subtype for the displayed documents. Since
  // `list_documents` began projecting these inline (migration 20260521184750)
  // they arrive with the rows in a single round-trip; this cache just holds
  // them so inline-edit cells can apply optimistic patches.
  const [systemValuesByDoc, setSystemValuesByDoc] = useState<
    Map<string, DocumentSystemRowValues>
  >(() => new Map());

  const mountedRef = useRef(true);
  const fetchTokenRef = useRef(0);

  // ----- Resolve user + facet options, scoped to the shell's active org ---
  // Re-runs whenever the active org changes (switch / create / invite-hint),
  // so the capability + every facet pool reload for the org the user is
  // actually in. Previously this ran once on mount and pinned to the oldest
  // membership, which is why switching orgs still showed the old org's docs.
  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }

      currentUserIdRef.current = session.user.id;

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      // Capability for THIS org's membership (gates the + New button).
      const { data: memRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role_id')
        .eq('user_id', session.user.id)
        .eq('org_id', activeOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load your documents. Please try again.");
        return;
      }

      const permissionRoleId =
        (memRow as { permission_role_id: string | null } | null)
          ?.permission_role_id ?? null;
      if (permissionRoleId) {
        const { data: pg } = await supabase
          .from('permission_groups')
          .select('system_key, capability_keys')
          .eq('id', permissionRoleId)
          .maybeSingle();
        if (!cancelled && pg) {
          const systemKey = (pg as { system_key: string | null }).system_key;
          const keys =
            (pg as { capability_keys: string[] | null }).capability_keys ?? [];
          const isPrivileged = systemKey === 'owner' || systemKey === 'admin';
          setCanCreate(isPrivileged || keys.includes('documents.create'));
        }
      } else {
        setCanCreate(false);
      }

      // Subtype options now live in `document_property_options` for the org's
      // Subtype property def (Phase 4). Load the system defs + their options
      // alongside the existing facet pools so inline edits + the Subtype
      // dropdown have everything in one place.
      const [typesRes, membersRes, sysDefsRes] = await Promise.all([
        supabase
          .from('document_types')
          .select('id, name, key')
          .eq('org_id', activeOrgId)
          .is('archived_at', null)
          .order('name', { ascending: true }),
        supabase.rpc('list_org_members', { p_org_id: activeOrgId }),
        supabase
          .from('document_property_defs')
          .select('*')
          .eq('org_id', activeOrgId)
          .is('archived_at', null)
          .in('key', [
            SYSTEM_OWNER_KEY,
            SYSTEM_LIFECYCLE_KEY,
            SYSTEM_SUBTYPE_KEY
          ])
      ]);
      if (cancelled) return;

      if (!typesRes.error && typesRes.data) {
        setTypeOptions(
          (typesRes.data as ReadonlyArray<{
            id: string;
            name: string;
            key: string;
          }>).map((r) => ({ id: r.id, name: r.name, key: r.key }))
        );
      }

      // Cache the system defs by key for inline-edit lookups.
      const sysDefs = (sysDefsRes.data ?? []) as DocumentPropertyDefRow[];
      systemDefsRef.current = new Map(sysDefs.map((d) => [d.key, d] as const));

      // Load the Subtype + Lifecycle options for those defs (both are
      // `single_select`). Owner is `kind='user'` — no options needed.
      const selectDefIds = sysDefs
        .filter((d) => d.kind === 'single_select')
        .map((d) => d.id);
      if (selectDefIds.length > 0) {
        const { data: optRows } = await supabase
          .from('document_property_options')
          .select('*')
          .in('property_def_id', selectDefIds)
          .is('archived_at', null)
          .order('position', { ascending: true });
        if (cancelled) return;
        const optsByDef = new Map<string, DocumentPropertyOptionRow[]>();
        for (const o of (optRows ?? []) as DocumentPropertyOptionRow[]) {
          const bucket = optsByDef.get(o.property_def_id) ?? [];
          bucket.push(o);
          optsByDef.set(o.property_def_id, bucket);
        }
        systemOptionsRef.current = optsByDef;
        // Surface Subtype options for the filter facet + table dropdown.
        const subtypeDef = systemDefsRef.current.get(SYSTEM_SUBTYPE_KEY);
        if (subtypeDef) {
          const subtypeOpts = optsByDef.get(subtypeDef.id) ?? [];
          setSubtypeOptions(
            subtypeOpts.map((o) => ({
              id: o.id,
              name: o.label,
              slug: o.key
            }))
          );
        } else {
          setSubtypeOptions([]);
        }
      } else {
        systemOptionsRef.current = new Map();
        setSubtypeOptions([]);
      }
      if (!membersRes.error && membersRes.data) {
        type RawMember = {
          kind?: string | null;
          user_id?: string | null;
          display_name?: string | null;
        };
        const opts: DocumentMemberOption[] = [];
        for (const r of membersRes.data as ReadonlyArray<RawMember>) {
          if (!r.user_id) continue;
          opts.push({
            userId: r.user_id,
            displayName: r.display_name ?? ''
          });
        }
        setMemberOptions(opts);
      }

      setStatus('ready');
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [activeOrgId]);

  // ----- Fetch documents whenever state / org changes --------------------
  const fetchRows = useCallback(
    async (orgId: string, fetchState: DocumentListState) => {
      const token = ++fetchTokenRef.current;
      setLoading(true);
      setError(null);

      const args = buildRpcArgs(fetchState, orgId, projectId);
      const { data, error: qErr } = await supabase.rpc('list_documents', args);

      if (!mountedRef.current) return;
      if (token !== fetchTokenRef.current) return;

      if (qErr) {
        setLoading(false);
        setError("Couldn't load documents. Please try again.");
        return;
      }
      const rpcRows = (data ?? []) as ReadonlyArray<ListDocumentsRow>;
      const nextTotal = rpcRows[0]?.total_count ?? 0;

      // `list_documents` projects Owner / Lifecycle / Subtype inline
      // (migration 20260521184750), so the system properties arrive with the
      // rows in this single round-trip — no separate fetch, no mount-time race.
      const visibleRows: DocumentRow[] = [];
      const sysValues = new Map<string, DocumentSystemRowValues>();
      for (const r of rpcRows) {
        const { total_count, owner_id, lifecycle, subtype_id, ...row } = r;
        void total_count;
        visibleRows.push(row as DocumentRow);
        sysValues.set(r.id, {
          ownerId: owner_id ?? null,
          lifecycle: asLifecycle(lifecycle),
          subtypeId: subtype_id ?? null
        });
      }
      setRows(visibleRows);
      setTotal(Number(nextTotal));
      setSystemValuesByDoc(sysValues);
      setLoading(false);
    },
    [projectId]
  );

  useEffect(() => {
    if (!activeOrgId) return;
    void fetchRows(activeOrgId, state);
  }, [activeOrgId, state, fetchRows]);


  // ----- Actions: every mutator updates state + URL ----------------------
  const setFilter = useCallback<DocumentsListActions['setFilter']>(
    (key, value) => {
      setState((prev) => {
        const next: DocumentListState = { ...prev, page: 1 };
        switch (key) {
          case 'type':
          case 'owner': {
            const arr = Array.isArray(value)
              ? (value as ReadonlyArray<string>)
              : value
                ? [value as string]
                : [];
            next[key] = arr;
            break;
          }
          case 'lifecycle': {
            const arr = Array.isArray(value)
              ? (value as ReadonlyArray<string>)
              : value
                ? [value as string]
                : [];
            next.lifecycle = arr.filter(
              (v): v is DocumentLifecycle =>
                v === 'draft' || v === 'published' || v === 'archived'
            );
            break;
          }
          case 'created_after':
            next.createdAfter = typeof value === 'string' ? value : null;
            break;
          case 'created_before':
            next.createdBefore = typeof value === 'string' ? value : null;
            break;
          case 'updated_after':
            next.updatedAfter = typeof value === 'string' ? value : null;
            break;
          case 'updated_before':
            next.updatedBefore = typeof value === 'string' ? value : null;
            break;
        }
        return next;
      });
    },
    []
  );

  const removeFilter = useCallback<DocumentsListActions['removeFilter']>(
    (key, value) => {
      setState((prev) => {
        const next: DocumentListState = { ...prev, page: 1 };
        switch (key) {
          case 'type':
          case 'owner': {
            const list = prev[key];
            next[key] =
              value === undefined ? [] : list.filter((v) => v !== value);
            break;
          }
          case 'lifecycle': {
            const list = prev.lifecycle;
            next.lifecycle =
              value === undefined
                ? []
                : list.filter((v) => v !== (value as DocumentLifecycle));
            break;
          }
          case 'created_after':
            next.createdAfter = null;
            break;
          case 'created_before':
            next.createdBefore = null;
            break;
          case 'updated_after':
            next.updatedAfter = null;
            break;
          case 'updated_before':
            next.updatedBefore = null;
            break;
        }
        return next;
      });
    },
    []
  );

  const setSort = useCallback<DocumentsListActions['setSort']>((sort) => {
    setState((prev) => {
      const next = { ...prev, sort, page: 1 };
      return next;
    });
  }, []);

  const setSearch = useCallback<DocumentsListActions['setSearch']>((q) => {
    setState((prev) => {
      const next = { ...prev, q, page: 1 };
      return next;
    });
  }, []);

  const setPage = useCallback<DocumentsListActions['setPage']>((page) => {
    setState((prev) => {
      if (!Number.isFinite(page) || page < 1) return prev;
      const next = { ...prev, page };
      return next;
    });
  }, []);

  const setTab = useCallback<DocumentsListActions['setTab']>((tab) => {
    setState((prev) => {
      if (prev.tab === tab) return prev;
      const next = { ...prev, tab };
      return next;
    });
  }, []);

  const setGroupBy = useCallback<DocumentsListActions['setGroupBy']>(
    (groupBy) => {
      setState((prev) => {
        if (prev.groupBy === groupBy) return prev;
        const next = { ...prev, groupBy };
        return next;
      });
    },
    []
  );

  const setCols = useCallback<DocumentsListActions['setCols']>((cols) => {
    setState((prev) => {
      const next = { ...prev, cols };
      return next;
    });
  }, []);

  const archiveDocument = useCallback<
    DocumentsListActions['archiveDocument']
  >(
    async (id) => {
      // Optimistic remove when archived isn't in the active lifecycle facet.
      const showingArchived = state.lifecycle.includes('archived');
      const optimistic = !showingArchived;
      if (optimistic) {
        setRows((prev) => prev.filter((r) => r.id !== id));
        setTotal((t) => Math.max(0, t - 1));
      }
      const { error: rpcErr } = await supabase.rpc('archive_document', {
        p_document_id: id
      });
      if (rpcErr) {
        if (activeOrgId) void fetchRows(activeOrgId, state);
        return { ok: false, error: rpcErr.message };
      }
      if (activeOrgId) void fetchRows(activeOrgId, state);
      return { ok: true };
    },
    [activeOrgId, fetchRows, state]
  );

  const archiveDocuments = useCallback<
    DocumentsListActions['archiveDocuments']
  >(
    async (ids) => {
      if (ids.length === 0) return { ok: true, failed: [] };
      const showingArchived = state.lifecycle.includes('archived');
      const optimistic = !showingArchived;
      const idSet = new Set(ids);
      if (optimistic) {
        setRows((prev) => prev.filter((r) => !idSet.has(r.id)));
        setTotal((t) => Math.max(0, t - ids.length));
      }
      const results = await Promise.all(
        ids.map((id) =>
          supabase.rpc('archive_document', { p_document_id: id })
        )
      );
      const failed: string[] = [];
      results.forEach((r, i) => {
        if (r.error) failed.push(ids[i]);
      });
      if (activeOrgId) void fetchRows(activeOrgId, state);
      return { ok: failed.length === 0, failed };
    },
    [activeOrgId, fetchRows, state]
  );

  const restoreDocument = useCallback<
    DocumentsListActions['restoreDocument']
  >(
    async (id) => {
      const { error: rpcErr } = await supabase.rpc('restore_document', {
        p_document_id: id
      });
      if (rpcErr) return { ok: false, error: rpcErr.message };
      if (activeOrgId) void fetchRows(activeOrgId, state);
      return { ok: true };
    },
    [activeOrgId, fetchRows, state]
  );

  // Inline-edit setters (lifecycle / subtype / owner) now write directly to
  // `custom_attribute_values` keyed by `(document_id, property_def_id)`. The
  // legacy `set_document_*` RPCs are gone (Phases 2/3/4) — the documents row
  // no longer carries those columns, the def id lookup happens via the
  // workspace-pool property defs cached on mount, and the `.upsert()` is
  // gated by the grants + RLS policies on `custom_attribute_values`.
  const upsertSystemValue = useCallback(
    async (
      docId: string,
      defKey: typeof SYSTEM_OWNER_KEY | typeof SYSTEM_LIFECYCLE_KEY | typeof SYSTEM_SUBTYPE_KEY,
      jsonValue: Record<string, unknown> | null
    ): Promise<{ ok: boolean; error?: string }> => {
      const def = systemDefsRef.current.get(defKey);
      if (!def) {
        return { ok: false, error: `${defKey} property not configured` };
      }
      const orgId = activeOrgId;
      if (!orgId) return { ok: false, error: 'No active workspace' };

      // Optimistic update to the local value cache so the cell reflects the
      // change before the round-trip completes.
      setSystemValuesByDoc((prev) => {
        const next = new Map(prev);
        const current = next.get(docId) ?? {
          ownerId: null,
          lifecycle: null,
          subtypeId: null
        };
        const patched: DocumentSystemRowValues = { ...current };
        if (defKey === SYSTEM_OWNER_KEY) {
          patched.ownerId = (jsonValue?.user_id as string | undefined) ?? null;
        } else if (defKey === SYSTEM_SUBTYPE_KEY) {
          patched.subtypeId =
            (jsonValue?.option_id as string | undefined) ?? null;
        } else {
          const optId = jsonValue?.option_id as string | undefined;
          const optKey = optId
            ? (systemOptionsRef.current.get(def.id) ?? []).find(
                (o) => o.id === optId
              )?.key
            : undefined;
          patched.lifecycle = asLifecycle(optKey ?? null);
        }
        next.set(docId, patched);
        return next;
      });

      const { error: upsertErr } = await supabase
        .from('custom_attribute_values')
        .upsert(
          {
            document_id: docId,
            property_def_id: def.id,
            org_id: orgId,
            value: jsonValue as never
          },
          { onConflict: 'document_id,property_def_id' }
        );
      if (upsertErr) {
        // Revert by re-fetching authoritative state.
        if (activeOrgId) void fetchRows(activeOrgId, state);
        return { ok: false, error: upsertErr.message };
      }
      return { ok: true };
    },
    [activeOrgId, fetchRows, state]
  );

  const setLifecycle = useCallback<DocumentsListActions['setLifecycle']>(
    async (id, lifecycle) => {
      // Resolve the lifecycle option_id from the well-known option key
      // ('draft' / 'published' / 'archived').
      const def = systemDefsRef.current.get(SYSTEM_LIFECYCLE_KEY);
      if (!def) return { ok: false, error: 'Lifecycle property not configured' };
      const opt = (systemOptionsRef.current.get(def.id) ?? []).find(
        (o) => o.key === lifecycle
      );
      if (!opt) {
        return { ok: false, error: `Unknown lifecycle: ${lifecycle}` };
      }
      return upsertSystemValue(id, SYSTEM_LIFECYCLE_KEY, { option_id: opt.id });
    },
    [upsertSystemValue]
  );

  const setSubtype = useCallback<DocumentsListActions['setSubtype']>(
    async (id, subtypeId) => {
      return upsertSystemValue(
        id,
        SYSTEM_SUBTYPE_KEY,
        subtypeId ? { option_id: subtypeId } : null
      );
    },
    [upsertSystemValue]
  );

  const setOwner = useCallback<DocumentsListActions['setOwner']>(
    async (id, userId) => {
      return upsertSystemValue(
        id,
        SYSTEM_OWNER_KEY,
        userId ? { user_id: userId } : null
      );
    },
    [upsertSystemValue]
  );

  const setDocumentType = useCallback<DocumentsListActions['setDocumentType']>(
    async (id, typeId) => {
      const prevRow = rows.find((r) => r.id === id);
      // Optimistic patch — Type is a plain `type_id` column on the row (not a
      // custom-attribute value like Owner/Lifecycle/Subtype).
      setRows((prev) =>
        prev.map((r) =>
          r.id === id ? ({ ...r, type_id: typeId } as DocumentRow) : r
        )
      );
      const { error: rpcErr } = await supabase.rpc('set_document_type', {
        p_document_id: id,
        p_type_id: typeId,
        // No editor stamp here; the inline cell skips optimistic-concurrency.
        p_expected_updated_at: null
      });
      if (rpcErr) {
        if (prevRow) {
          setRows((prev) => prev.map((r) => (r.id === id ? prevRow : r)));
        }
        if (activeOrgId) void fetchRows(activeOrgId, state);
        return { ok: false, error: rpcErr.message };
      }
      return { ok: true };
    },
    [rows, activeOrgId, fetchRows, state]
  );

  const createDocument = useCallback<DocumentsListActions['createDocument']>(
    async ({ title, typeId }) => {
      const trimmed = title.trim();
      if (trimmed.length === 0 || trimmed.length > 200) return { ok: false };
      const orgId = activeOrgId;
      if (!orgId) return { ok: false };
      const { data, error: rpcErr } = await supabase.rpc('create_document', {
        p_org_id: orgId,
        p_title: trimmed,
        p_type_id: typeId ?? undefined
      });
      if (!mountedRef.current) return { ok: false };
      if (rpcErr) return { ok: false };
      const envelope = data as
        | {
            ok: true;
            document: {
              id: string;
              title: string;
              type_id: string;
              last_event_at: string;
              created_at: string;
            };
          }
        | { ok: false; error: 'invalid_title' | 'invalid_type' | 'forbidden' };
      if (!envelope || envelope.ok !== true) return { ok: false };
      const userId = currentUserIdRef.current;
      // After Phases 1–4, documents columns are just the minimal set —
      // lifecycle / subtype / owner / status all live in custom_attribute_values
      // keyed by the org's matching workspace-pool property_defs.
      const newRow = {
        id: envelope.document.id,
        org_id: orgId,
        title: envelope.document.title,
        body: '',
        body_preview: '',
        type_id: envelope.document.type_id,
        archived_at: null,
        created_at: envelope.document.created_at,
        created_by_id: userId,
        last_event_at: envelope.document.last_event_at,
        last_event_by_id: userId,
        embedding: null as unknown as DocumentRow['embedding'],
        search_tsv: null as unknown as DocumentRow['search_tsv']
      } as DocumentRow;
      setRows((prev) => [newRow, ...prev]);
      setTotal((t) => t + 1);
      return { ok: true, id: envelope.document.id };
    },
    [activeOrgId]
  );

  const reload = useCallback(() => {
    if (activeOrgId) void fetchRows(activeOrgId, state);
  }, [activeOrgId, fetchRows, state]);

  const actions = useMemo<DocumentsListActions>(
    () => ({
      setFilter,
      removeFilter,
      setSort,
      setSearch,
      setPage,
      setTab,
      setGroupBy,
      setCols,
      archiveDocument,
      archiveDocuments,
      restoreDocument,
      setLifecycle,
      setSubtype,
      setOwner,
      setDocumentType,
      createDocument,
      reload
    }),
    [
      setFilter,
      removeFilter,
      setSort,
      setSearch,
      setPage,
      setTab,
      setGroupBy,
      setCols,
      archiveDocument,
      archiveDocuments,
      restoreDocument,
      setLifecycle,
      setSubtype,
      setOwner,
      setDocumentType,
      createDocument,
      reload
    ]
  );

  // Every visible row gets a system-values entry — defaulting to all-null for
  // documents with no Owner / Lifecycle / Subtype set. `systemValuesByDoc` is
  // already resolved (the `list_documents` RPC projects the values inline).
  const resolvedSystemValues = useMemo<ReadonlyMap<string, DocumentSystemRowValues>>(() => {
    const out = new Map<string, DocumentSystemRowValues>();
    for (const r of rows) {
      out.set(
        r.id,
        systemValuesByDoc.get(r.id) ?? {
          ownerId: null,
          lifecycle: null,
          subtypeId: null
        }
      );
    }
    return out;
  }, [rows, systemValuesByDoc]);

  const derived = useMemo<DocumentsListDerived>(
    () => ({
      rows,
      total,
      pageSize: PAGE_SIZE,
      typeOptions,
      memberOptions,
      subtypeOptions,
      loading,
      error,
      hasActiveFilters: hasActiveFilters(state),
      canCreate,
      systemValuesByDoc: resolvedSystemValues
    }),
    [
      rows,
      total,
      typeOptions,
      memberOptions,
      subtypeOptions,
      loading,
      error,
      state,
      canCreate,
      resolvedSystemValues
    ]
  );

  return {
    status,
    state,
    activeOrgId,
    activeOrgName,
    derived,
    actions
  };
}
